import React, { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Edit3,
  ImageIcon,
  Loader2,
  Mail,
  Phone,
  Plus,
  RefreshCw,
  Search,
  UploadCloud,
  UserCheck,
  UserX,
  X,
} from "lucide-react";

import {
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useLazyGetEmployeeQuery,
  useLazyGetEmployeeByServiceIdQuery,
  useLazyGetAllServiceQuery,
  useUploadFileMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";

import { IServiceEmployee } from "../../../types/Response/AdminResponse";

export default function ManageEmployees() {
  // ----------------------------------------------------
  // API Hooks
  // ----------------------------------------------------
  const [triggerGetEmployees, { data: employeesData = [], isLoading: isEmployeesLoading, isError }] =
    useLazyGetEmployeeQuery();

  const [triggerGetServices, { data: services = [] }] = useLazyGetAllServiceQuery();

  const [createEmployee, { isLoading: isCreating }] = useCreateEmployeeMutation();
  const [updateEmployee, { isLoading: isUpdating }] = useUpdateEmployeeMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();

  const isSaving = isCreating || isUpdating || isUploading;

  // ----------------------------------------------------
  // Local States
  // ----------------------------------------------------
  const [selectedServiceId, setSelectedServiceId] = useState<number | "ALL">("ALL");
  const [searchTerm, setSearchTerm] = useState("");

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setFormMode] = useState<"add" | "edit">("add");
  const [editingEmployeeId, setEditingEmployeeId] = useState<number | null>(null);

  // Form State
  const [form, setForm] = useState({
    serviceId: 0,
    name: "",
    phoneNumber: "",
    email: "",
    imageUrl: "",
    isActive: true,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initial Fetch
  useEffect(() => {
    triggerGetEmployees();
    triggerGetServices();
  }, [triggerGetEmployees, triggerGetServices]);

  // ----------------------------------------------------
  // Handlers & Image Upload
  // ----------------------------------------------------
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const response = await uploadFile({
        File: file,
        FolderName: "Employees/Avatars",
      }).unwrap();

      setForm((prev) => ({ ...prev, imageUrl: response.fileUrl }));
    } catch (error) {
      console.error("Failed to upload avatar:", error);
      alert("Image upload failed. Please try again.");
    }
  };

  const handleOpenAddModal = () => {
    setFormMode("add");
    setEditingEmployeeId(null);
    setForm({
      serviceId: services.length > 0 ? services[0].id : 0,
      name: "",
      phoneNumber: "",
      email: "",
      imageUrl: "",
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (emp: IServiceEmployee) => {
    setFormMode("edit");
    setEditingEmployeeId(emp.employeeId);
    setForm({
      serviceId: emp.serviceId,
      name: emp.name,
      phoneNumber: emp.phoneNumber || "",
      email: emp.email || "",
      imageUrl: emp.imageUrl || "",
      isActive: emp.isActive,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phoneNumber || !form.serviceId) {
      alert("Please fill in Name, Service, and Phone Number.");
      return;
    }

    try {
      if (modalMode === "edit" && editingEmployeeId) {
        await updateEmployee({
          employeeId: editingEmployeeId,
          ...form,
        } as any).unwrap();
        alert("Employee updated successfully!");
      } else {
        await createEmployee(form as any).unwrap();
        alert("Employee created successfully!");
      }
      setIsModalOpen(false);
      triggerGetEmployees();
    } catch (err) {
      console.error("Employee action failed:", err);
      alert("Unable to save employee details.");
    }
  };

  // Filter Employees by Service & Search String
  const filteredEmployees = employeesData.filter((emp) => {
    const matchesService =
      selectedServiceId === "ALL" || emp.serviceId === selectedServiceId;
    const matchesSearch =
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.phoneNumber?.includes(searchTerm) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesService && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-8 font-nunito">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Employee Management</h1>
            <p className="text-xs text-slate-500 mt-1">
              Manage service staff members, contact details, and assigned service sectors.
            </p>
          </div>

          <button
            onClick={handleOpenAddModal}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <Plus className="w-4 h-4 stroke-[2.5]" />
            Add Staff Member
          </button>
        </div>

        {/* Filter & Search Toolbar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-4 rounded-xl border border-slate-200">
          {/* Service Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
            <button
              onClick={() => setSelectedServiceId("ALL")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition cursor-pointer ${
                selectedServiceId === "ALL"
                  ? "bg-slate-800 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              All Staff
            </button>
            {services.map((srv) => (
              <button
                key={srv.id}
                onClick={() => setSelectedServiceId(srv.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition cursor-pointer ${
                  selectedServiceId === srv.id
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {srv.name}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search staff by name/phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:border-emerald-500 bg-slate-50/50"
            />
          </div>
        </div>

        {/* Employee Cards Grid */}
        {isEmployeesLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-xs font-semibold text-slate-400 mt-2">Loading Staff Directory...</p>
          </div>
        ) : isError ? (
          <div className="bg-white p-8 rounded-2xl text-center border border-rose-200 max-w-md mx-auto">
            <AlertCircle className="w-8 h-8 text-rose-500 mx-auto" />
            <p className="text-sm font-bold text-slate-800 mt-2">Failed to load staff list</p>
            <button
              onClick={() => triggerGetEmployees()}
              className="mt-3 text-xs bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold cursor-pointer inline-flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        ) : filteredEmployees.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredEmployees.map((emp) => (
              <div
                key={emp.employeeId}
                className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col justify-between hover:shadow-md transition shadow-2xs group relative overflow-hidden"
              >
                <div className="space-y-3">
                  {/* Top Row: Avatar & Status Badge */}
                  <div className="flex items-start justify-between">
                    <img
                      src={
                        emp.imageUrl ||
                        "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150"
                      }
                      alt={emp.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-slate-100 shadow-2xs"
                    />

                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                        emp.isActive
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-500 border-slate-200"
                      }`}
                    >
                      {emp.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  {/* Info */}
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm group-hover:text-emerald-700 transition">
                      {emp.name}
                    </h3>
                    <p className="text-xs text-emerald-600 font-semibold mt-0.5">
                      {emp.serviceName || "Service Staff"}
                    </p>
                  </div>

                  <div className="space-y-1.5 text-xs text-slate-500 pt-1 border-t border-slate-100">
                    <p className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-slate-400" />
                      <span>{emp.phoneNumber || "No phone registered"}</span>
                    </p>
                    <p className="flex items-center gap-1.5 truncate">
                      <Mail className="w-3.5 h-3.5 text-slate-400" />
                      <span className="truncate">{emp.email || "No email"}</span>
                    </p>
                  </div>
                </div>

                {/* Edit Button Footer */}
                <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
                  <button
                    onClick={() => handleOpenEditModal(emp)}
                    className="p-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-slate-600 hover:text-slate-900 text-xs font-semibold flex items-center gap-1 cursor-pointer transition"
                  >
                    <Edit3 className="w-3.5 h-3.5" /> Edit Profile
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-400">
            No employees found matching the selected criteria.
          </div>
        )}
      </div>

      {/* ---------------------------------------------------- */}
      {/* MODAL: CREATE / EDIT EMPLOYEE                        */}
      {/* ---------------------------------------------------- */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-lg font-bold text-slate-800">
                {modalMode === "edit" ? "Edit Staff Profile" : "Add New Staff Member"}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Avatar Uploader */}
              <div className="flex flex-col items-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  className="hidden"
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 rounded-full border-2 border-dashed border-slate-300 hover:border-emerald-500 overflow-hidden cursor-pointer flex items-center justify-center relative group bg-slate-50"
                >
                  {form.imageUrl ? (
                    <>
                      <img src={form.imageUrl} alt="Avatar" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                        <UploadCloud className="w-5 h-5 text-white" />
                      </div>
                    </>
                  ) : (
                    <ImageIcon className="w-6 h-6 text-slate-400" />
                  )}
                </div>
                <span className="text-[10px] text-slate-400 font-semibold mt-1">
                  Click to upload photo
                </span>
              </div>

              {/* Name Input */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Rahul Sharma"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                />
              </div>

              {/* Service Selection */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Assigned Service *
                </label>
                <select
                  required
                  value={form.serviceId}
                  onChange={(e) => setForm({ ...form, serviceId: Number(e.target.value) })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 bg-white cursor-pointer"
                >
                  <option value={0}>Select Service</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="+91 98765 43210"
                    value={form.phoneNumber}
                    onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    placeholder="staff@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Status Switch */}
              <div className="flex items-center justify-between pt-2">
                <span className="text-xs font-semibold text-slate-600">Active Status</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, isActive: !form.isActive })}
                  className={`w-9 h-5 rounded-full relative transition cursor-pointer ${
                    form.isActive ? "bg-emerald-600" : "bg-slate-300"
                  }`}
                >
                  <div
                    className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${
                      form.isActive ? "translate-x-4" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                >
                  {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {modalMode === "edit" ? "Update Staff" : "Save Staff"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}